import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { translateContent } from '~src/services/translationServices';

import { MatrixReportResponseRaw } from '~src/interfaces/heatmap.interface';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { HeatmapListFilters } from '~src/modules/heatmap/heatmap.types';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { reportingApi } from '~src/services/ApiController';
import { AsyncAction, isSuccess, ThunkAnyAction, ThunkMWDispatch } from '~src/services/apiMiddleware';
import { getUrlFromObject } from '~src/utils';

import {
  fetchOptionsOfAttr,
  fetchQuestionsMatrix,
  fetchTopicsMatrix,
  RESET_MATRIX,
  SURVEY_CHANGE,
} from './action.types';

type UrlParams = HeatmapListFilters & {
  filterSurvey?: string | null;
  filterSubject?: string;
  filterAnsweredRange?: PeriodValues | null;
};

const FETCH_MATRIX_CHUNK_SIZE = 100;

export const fetchMatrixDistributor = async (
  dispatch: ThunkMWDispatch,
  attributeId: string,
  surveyType: SurveySubjectTypes,
  params: HeatmapListFilters,
) => {
  const combinedParams: UrlParams = { ...params };
  // extract the selected survey and concat it in one property
  combinedParams.filterAnsweredRange = params.filterAnsweredRange || PeriodValues.AllTime;
  combinedParams.filterSubject = surveyType || SurveySubjectTypes.General;
  combinedParams.filterSurvey = params.surveyPulse || params.surveyGroup || params.surveyPeople;

  if (params.surveyPulse) {
    combinedParams.filterSubject = SurveySubjectTypes.General;
  }
  if (params.surveyGroup) {
    combinedParams.filterSubject = SurveySubjectTypes.Groups;
  }
  if (params.surveyPeople) {
    combinedParams.filterSubject = SurveySubjectTypes.People;
  }

  combinedParams.surveyPulse = null;
  combinedParams.surveyGroup = null;
  combinedParams.surveyPeople = null;

  // if a group is selected store and delete it from the params because we only need it to fetch the options
  let selectedGroup;
  if (combinedParams.selectedGroup) {
    selectedGroup = combinedParams.selectedGroup;
    combinedParams.selectedGroup = null;
  }

  // If a group is selected:
  if (selectedGroup) {
    combinedParams.attribute = null;
    const queryString = getUrlFromObject({ ...combinedParams });
    // API REQUEST
    if (combinedParams.filterTopicId) {
      return dispatch(fetchMatrixQuestionsOfGroup(queryString, selectedGroup));
    }
    return dispatch(fetchMatrixTopicsOfGroup(queryString, selectedGroup));
  }

  // If NO group is selected, fetch the options of the segment
  const res: ThunkAnyAction<Segment[]> = await dispatch(
    fetchGroups(
      attributeId,
      combinedParams.filterSurvey || undefined,
      combinedParams.filterSubject,
      combinedParams.filterAnsweredRange,
    ),
  );

  if (isSuccess(res)) {
    combinedParams.attribute = null;

    const segments = res.response.data.map((segment) => segment.id);

    const queries = _.chunk(segments, FETCH_MATRIX_CHUNK_SIZE).map((chunk) => {
      // add the options ids to the params to add it to the query string
      const urlParams: { [key: string]: string | null | undefined } = {
        ...combinedParams,
        filterAttributeOptions: chunk.join(','),
      };
      return getUrlFromObject(urlParams);
    });

    if (combinedParams && combinedParams.filterTopicId) {
      return dispatch(fetchMatrixQuestions(queries));
    }
    return dispatch(fetchMatrixTopics(queries));
  }
};

export const fetchGroups = (
  attributeId: string,
  surveyId?: string,
  surveyType?: string,
  filterAnsweredRange?: PeriodValues,
): AsyncAction<Segment[], undefined> => ({
  types: fetchOptionsOfAttr,
  callAPI: () => {
    if (surveyId) {
      surveyType = undefined;
    }
    if (filterAnsweredRange === PeriodValues.AllTime) {
      filterAnsweredRange = undefined;
    }
    const params: { [key: string]: string | undefined } = {
      attribute: attributeId,
      survey: surveyId,
      survey_type: surveyType,
      filterAnsweredRange,
    };
    return reportingApi.get(`groups${getUrlFromObject(params)}`);
  },
});

function getComparableName(s: Segment): string {
  switch(s.type) {
    case SegmentType.Company: return s.meta.name;
    case SegmentType.Group: return translateContent(s.meta.option_name);
    case SegmentType.Team: return s.meta.manager_name;
    case SegmentType.User: return s.meta.name;
    case SegmentType.Anonymous: return '?';
  }
}

export function mergeCompareResponses(
  responses: Array<AxiosResponse<MatrixReportResponseRaw>>,
): AxiosResponse<MatrixReportResponseRaw> {
  const masterResponse = responses[0];

  responses.slice(1).forEach((partialResponse) => {
    const masterResult = masterResponse.data.result;
    const partialResult = partialResponse.data.result;
    if (partialResult && partialResult.segment_aggregations) {
      masterResult.segment_aggregations = masterResult.segment_aggregations || [];
      masterResult.segment_aggregations = masterResult.segment_aggregations.concat(
        partialResult.segment_aggregations,
      );
    }
    if (partialResult && partialResult.segments) {
      masterResult.segments = masterResult.segments || [];
      masterResult.segments = masterResult.segments.concat(partialResult.segments);
    }
  });

  masterResponse.data.result.segments = masterResponse.data.result.segments.sort((a, b) => {
    const aName = getComparableName(a);
    const bName = getComparableName(b);

    return aName.localeCompare(bName);
  });

  return masterResponse;
}

async function fetchMatrix(
  uris: string[],
  abortController: AbortController,
): Promise<AxiosResponse<MatrixReportResponseRaw>> {
  if (uris.length === 0) {
    throw new Error('call with empty queries string');
  }
  const responses: Array<AxiosResponse<MatrixReportResponseRaw>> = await Promise.all(
    uris.map((uri) => {
      return reportingApi.get<MatrixReportResponseRaw>(uri, { signal: abortController.signal });
    }),
  );

  return mergeCompareResponses(responses);
}

export const fetchMatrixTopics = (
  queries: string[],
): AsyncAction<MatrixReportResponseRaw, { abortController: AbortController }> => {
  const abortController = new AbortController();
  return {
    types: fetchTopicsMatrix,
    callAPI: () =>
      fetchMatrix(
        queries.map((q) => `reports/matrix/topics${q}`),
        abortController,
      ),
    payload: { abortController },
  };
};

export const fetchMatrixTopicsOfGroup = (
  queryString: string,
  selectedGroup: string,
): AsyncAction<MatrixReportResponseRaw, { selectedGroup: string; abortController: AbortController }> => {
  const abortController = new AbortController();
  return {
    types: fetchTopicsMatrix,
    callAPI: () =>
      reportingApi.get(`reports/matrix/groups/${selectedGroup}/topics${queryString}`, {
        signal: abortController.signal,
      }),
    payload: { selectedGroup, abortController },
  };
};

export const fetchMatrixQuestionsOfGroup = (
  queryString: string,
  selectedGroup: string,
): AsyncAction<MatrixReportResponseRaw, { selectedGroup: string; abortController: AbortController }> => {
  const abortController = new AbortController();
  return {
    types: fetchQuestionsMatrix,
    callAPI: () =>
      reportingApi.get(`reports/matrix/groups/${selectedGroup}/questions${queryString}`, {
        signal: abortController.signal,
      }),
    payload: { selectedGroup, abortController },
  };
};

export const fetchMatrixQuestions = (
  queries: string[],
): AsyncAction<MatrixReportResponseRaw, { abortController: AbortController }> => {
  const abortController = new AbortController();
  return {
    types: fetchQuestionsMatrix,
    callAPI: () =>
      fetchMatrix(
        queries.map((q) => `reports/matrix/questions${q}`),
        abortController,
      ),
    payload: { abortController },
  };
};

export const setMatrixSurvey = (survey?: string) => ({
  type: SURVEY_CHANGE,
  payload: { survey },
});

export const resetMatrix = () => ({ type: RESET_MATRIX });
